import { Button, ButtonVariant, ChangeEvent, ClickEvent, Image, ImageProps, Label, Panel, PanelProps, Switch } from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { RowPermsAllowDeny } from "../models/ModelPermsAllowDeny";
import { GroupSecureItemTooltip } from "./GroupSecureItemTooltip";
import { PanelPermsGrant, UserOrGroupTable } from "./PanelPermsGrant";
import { PermissionsSource } from "./PermissionsSource";
import { PermsContainer } from "./PermsContainer";
import { PermsUserAndGroupCache } from "./PermsUserAndGroupCache ";
import { SecureItem } from "./SecureItem";
import { UserOrGroup } from "./UserOrGroup";

const switchImageProps: Partial<ImageProps> = {
    height: 22,
    width: 30,
    name: "threePeople"
};

export interface PanelSecureItemProps extends PanelProps {
    permsGrant: PanelPermsGrant;
}

export abstract class PanelSecureItem extends Panel implements PanelSecureItemProps {
    imageSwitchLeft: Image;
    imageSwitchRight: Image;
    labelDescription: Label;
    labelSecurityType: Label;
    panelAllowed: Panel;
    switchSecured: Switch;
    permsGrant: PanelPermsGrant;
    private _secured: boolean;
    private _syncingAllTables: boolean;

    abstract someAllowed: boolean;
    abstract alreadyAllowed(userOrGroup: UserOrGroup, id: string): boolean
    abstract isUserAllowedByGroupMembership(userId: string): boolean
    abstract syncLabelDescription(): void;
    protected abstract internalAddAllowed(userOrGroup: UserOrGroup, id: string): void;

    constructor(props: Partial<PanelProps>) {
        super({
            borderBottomWidth: 1,
            borderBottomColor : "subtle.lightest",
            fillHeight: false,
            width: 320,
            padding:0,
            marginBottom: 16,
            paddingBottom: 4,
            ...props
        });
        this.init();

        this.addClickListener(() => {
            if (this.permsGrant)
                this.permsGrant.selectedChild = this
        });
    }

    init() {
        this.labelSecurityType = new Label({
            fontBold: true,
            id: "labelSecurityType",
            minWidth: 140,
            marginRight: 4,
            rowBreak: false
        });

        this.imageSwitchLeft = new Image({
            ...switchImageProps,
            id: "imageSwitchLeft",
            fill: "primary",
            marginRight: 4,
            rowBreak: false,
        });

        this.switchSecured = new Switch({
            color: "primary",
            id: "switchSecured",
            marginRight: "4",
            rowBreak: false,
            leftCaption: "Off",
            rightCaption: "On"
        });
        this.switchSecured.addChangeListener(this.switchSecuredOnChange.bind(this));

        this.imageSwitchRight = new Image({
            ...switchImageProps,
            id: "imageSwitchRight",
            fill: "subtle",
        });

        this.labelDescription = new Label({
            color: "primary",
            id: "labelDescription"
        });

        this.panelAllowed = new Panel({
            id: "panelAllowed",
            minHeight: 32,
            fillRow: true
        });

        this.add(this.labelSecurityType);
        this.add(this.imageSwitchLeft);
        this.add(this.switchSecured);
        this.add(this.imageSwitchRight);
        this.add(this.labelDescription);
        this.add(this.panelAllowed);
    }

    get permissionsSource(): PermissionsSource {
        return this.permsGrant?.permissionsSource
    }

    get permsUserCache(): PermsUserAndGroupCache {
        return this.permissionsSource?.permsUserCache;
    }

    get permContainer(): PermsContainer {
        return this.permsGrant?.permsContainer;
    }

    set secured(value: boolean) {
        this._secured = value;
        this.syncUI();
    }

    get secured(): boolean {
        return this._secured;
    }

    syncUI() {
        this.switchSecured.checked = this.secured;
        this.imageSwitchLeft.style.visibility = !this.secured ? "visible" : "hidden";
        this.imageSwitchRight.style.visibility = this.secured ? "visible" : "hidden";
        this.panelAllowed.visible = this.secured;
        this.imageSwitchRight.name = this.secured ? (this.someAllowed ? "threePeopleBlueMiddle" : "threePeople") : null;
        this.syncLabelDescription();
    }

    switchSecuredOnChange(event: ChangeEvent) {
        if (!event.userInitiatedChange)
            return;
        this.secured = event.newValue;
    }

    syncTables() {
        try {
            this._syncingAllTables = true;
            this.syncTable(this.permsGrant.groupsTable);
            this.syncTable(this.permsGrant.lmeUsersTable);
            this.syncTable(this.permsGrant.webUsersTable);
        } finally {
            this._syncingAllTables = false;
        }
    }

    private internalAlreadyAllowed(userOrGroup: UserOrGroup, id: string): boolean {
        if (userOrGroup === "U" && this.isUserAllowedByGroupMembership(id)) {
            return true;
        }
        return this.alreadyAllowed(userOrGroup, id);
    }

    syncTable(userOrGroupTable: UserOrGroupTable) {
        if (userOrGroupTable == null)
            return;
        const {eligibleData: availableData, table, userOrGroup} = userOrGroupTable;
        table.clearRows();
        for (const row of Object.values(availableData)) {
            const id = row.get("id");
            if (!this.internalAlreadyAllowed(userOrGroup, id)) {
                const descr = userOrGroup === "U" ? row.get("name", id) : row.get("descr", id);
                table.addRow(new ModelRow(null, false, { descr, id }), null, { display: true });
            }
        }
        if (this._syncingAllTables !== true && userOrGroupTable === this.permsGrant.groupsTable) {
            this.syncTable(this.permsGrant.lmeUsersTable);
            this.syncTable(this.permsGrant.webUsersTable);
        }
    }

    addAllowed(userOrGroup: UserOrGroup, id: string) {
        if (this.internalAlreadyAllowed(userOrGroup, id))
            return;

        this.internalAddAllowed(userOrGroup, id);
        this.syncUI();
        const userOrGroupTable = this.permsGrant.getRelevantUserOrGroupTable(userOrGroup, id);
        this.syncTable(userOrGroupTable);
    }

    protected addAllowedToPermissionsSource(secureItem: SecureItem, userOrGroup: UserOrGroup, id: string): RowPermsAllowDeny {
        const row = secureItem.createRowPermsAllowDeny(id, userOrGroup);
        this.permissionsSource.addAllowDenyRow(row);
        secureItem.allowDenyChanged?.();
        return row;
    }

    createAllowItemPanel(row: RowPermsAllowDeny, deleteOnClick:() => void): Panel {
        const userOrGroup = row.get("user_or_group_flag") as UserOrGroup;
        const displayName = this.permsUserCache.getDisplayName(userOrGroup, row.get("user_or_group_id"));

        const result = new Panel({ fillRow: true, padding: 0 });
        const userLabel = new Label({ text: displayName, rowBreak: false, fillRow: true, imageColor: "primary" });
        const buttonDelete = new Button({ imageName: "x", variant: ButtonVariant.round, color: "subtle.darker" });
        userLabel.imageName = userOrGroup === "U" ? "personSolid" : "threePeople";
        buttonDelete.addClickListener(async (event: ClickEvent) => {
            deleteOnClick();
            const userOrGroupTable = this.permsGrant.getRelevantUserOrGroupTable(userOrGroup, row.get("user_or_group_id"));
            this.syncTable(userOrGroupTable);
            this.syncUI();
        });
        result.add(userLabel, buttonDelete);
        if (userOrGroup === "G") {
            GroupSecureItemTooltip.addTooltipCallback(result, row.get("user_or_group_id"), this.permsUserCache);
        }
        return result;
    }
}
